import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FileDown, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRecentAnalyses } from '@/hooks/useRecentAnalyses';
import { supabase } from '@/lib/supabase';

const SkeletonRow = () => (
    <TableRow>
        <TableCell>
            <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
            <Skeleton className="h-4 w-24" />
        </TableCell>
        <TableCell>
            <Skeleton className="h-6 w-20 rounded-full" />
        </TableCell>
        <TableCell>
            <div className="flex justify-end">
                <Skeleton className="h-4 w-8" />
            </div>
        </TableCell>
        <TableCell>
            <div className="flex justify-end">
                <Skeleton className="h-4 w-8" />
            </div>
        </TableCell>
        <TableCell>
            <div className="flex justify-end">
                <Skeleton className="h-4 w-8" />
            </div>
        </TableCell>
        <TableCell>
            <div className="flex justify-end">
                <Skeleton className="h-8 w-8 rounded-md" />
            </div>
        </TableCell>
    </TableRow>
);

const LoadingState = () => (
    <>
        {[...Array(5)].map((_, index) => (
            <SkeletonRow key={index} />
        ))}
    </>
);

const RecentAnalyses = () => {
    const { analyses, loading, error } = useRecentAnalyses(10);
    const [downloadingId, setDownloadingId] = useState(null);

    const truncateText = (text, maxLength) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return `${text.substring(0, maxLength)}...`;
    };

    const handleDownload = async (analysis) => {
        if (downloadingId) return;

        setDownloadingId(analysis.id);
        try {
            const { data, error } = await supabase
                .storage
                .from('analysis-files')
                .download(analysis.resultUrl);

            if (error) throw error;

            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date(analysis.date).toISOString().split('T')[0];
            a.download = `analysis_results_${date}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file. Please try again.');
        } finally {
            setDownloadingId(null);
        }
    };

    const renderMetric = (value, isCompleted) => {
        if (!isCompleted) {
            return (
                <div className="flex justify-end">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
            );
        }
        return <div className="text-right">{value}</div>;
    };

    if (error) {
        return (
            <Card className="h-full">
                <CardHeader>
                    <CardTitle>Recent Analyses</CardTitle>
                    <CardDescription className="text-red-500">
                        {error}
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="mb-1.5">Recent Analyses</CardTitle>
                    <CardDescription>
                        Your last 10 website analyses
                    </CardDescription>
                </div>
                <Button className="-translate-y-1.5" variant="outline" asChild>
                    <Link to="/history">View All</Link>
                </Button>
            </CardHeader>
            <CardContent className="flex-1">
                <div className="h-full overflow-auto">
                    <Table>
                        <TableHeader className="sticky top-0 bg-white dark:bg-gray-800">
                            <TableRow>
                                <TableHead>Date/Time</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Websites</TableHead>
                                <TableHead className="text-right">Matches</TableHead>
                                <TableHead className="text-right">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <span>Errors</span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Websites that couldn't be accessed. Credits for these have been refunded to your account.</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </TableHead>
                                <TableHead className="w-[60px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <LoadingState />
                            ) : analyses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-4">
                                        No analyses found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                analyses.map((analysis) => {
                                    const isCompleted = analysis.status === 'completed';
                                    return (
                                        <TableRow key={analysis.id}>
                                            <TableCell className="whitespace-nowrap">
                                                {new Date(analysis.date).toLocaleDateString()}, {new Date(analysis.date).toLocaleTimeString()}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap" title={analysis.analysis_name}>
                                                {truncateText(analysis.analysis_name, 15)}
                                            </TableCell>
                                            <TableCell className="py-4">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                        analysis.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                            'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {analysis.status.charAt(0).toUpperCase() + analysis.status.slice(1)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {analysis.websitesAnalyzed}
                                            </TableCell>
                                            <TableCell>
                                                {renderMetric(analysis.matchesFound, isCompleted)}
                                            </TableCell>
                                            <TableCell>
                                                {renderMetric(analysis.errorsCount, isCompleted)}
                                            </TableCell>
                                            <TableCell className="text-right p-2">
                                                {analysis.resultUrl && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDownload(analysis)}
                                                        disabled={downloadingId === analysis.id}
                                                        title="Download results"
                                                    >
                                                        {downloadingId === analysis.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <FileDown className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default RecentAnalyses;